import { useState } from 'react'
import apiClient from '@/shared/lib/apiClient'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { t } from '@/shared/lib/i18n'

export interface CreatedLogger {
  id: number
  label: string
}

interface AddLoggerInlineProps {
  /**
   * Instrument whose effective open-logging code gates the create on the server. Forwarded so the
   * Pundit policy resolves the instrument override (not just the tenant default). Omitted on the
   * multi-instrument dashboard list where no single instrument context applies to the picker.
   */
  instrumentId?: number | null
  /** Called with the freshly created logger so the caller can auto-select it. */
  onCreated: (logger: CreatedLogger) => void
}

/**
 * Inline "Add new user" control under the logger picker (REBUILD-531). Rendered only where open
 * logging is in effect — callers guard visibility; the server enforces the same via
 * Web::LoggerPolicy. First + Last are required; Employee/Badge ID is optional (blank allowed).
 */
export const AddLoggerInline = ({ instrumentId, onCreated }: AddLoggerInlineProps) => {
  const [open, setOpen] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [employeeId, setEmployeeId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSave = firstName.trim().length > 0 && lastName.trim().length > 0

  const reset = () => {
    setFirstName('')
    setLastName('')
    setEmployeeId('')
    setError(null)
  }

  const handleCancel = () => {
    setOpen(false)
    reset()
  }

  const handleSave = () => {
    if (!canSave || submitting) return
    setSubmitting(true)
    setError(null)
    apiClient
      .post<CreatedLogger>('/loggers', {
        instrument_id: instrumentId ?? undefined,
        logger: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          employee_id: employeeId.trim(),
        },
      })
      .then((response) => {
        onCreated({ id: response.data.id, label: response.data.label })
        setOpen(false)
        reset()
      })
      .catch(() => {
        setError(t('instruments.taskLogs.addLogger.error'))
      })
      .finally(() => setSubmitting(false))
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto p-0 text-sm"
        onClick={() => setOpen(true)}
      >
        {t('instruments.taskLogs.addLogger.trigger')}
      </Button>
    )
  }

  return (
    <div className="space-y-3 rounded-md border border-dashed p-3">
      <p className="text-sm font-medium">{t('instruments.taskLogs.addLogger.title')}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="add-logger-first-name">
            {t('instruments.taskLogs.addLogger.firstNameLabel')}
          </Label>
          <Input
            id="add-logger-first-name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="add-logger-last-name">
            {t('instruments.taskLogs.addLogger.lastNameLabel')}
          </Label>
          <Input
            id="add-logger-last-name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="add-logger-badge-id">
            {t('instruments.taskLogs.addLogger.badgeIdLabel')}{' '}
            <span className="text-muted-foreground font-normal">
              ({t('instruments.taskLogs.addLogger.badgeIdOptional')})
            </span>
          </Label>
          <Input
            id="add-logger-badge-id"
            value={employeeId}
            onChange={(event) => setEmployeeId(event.target.value)}
          />
        </div>
      </div>
      {error && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" onClick={handleSave} disabled={!canSave || submitting}>
          {submitting
            ? t('instruments.taskLogs.addLogger.saving')
            : t('instruments.taskLogs.addLogger.save')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleCancel}
          disabled={submitting}
        >
          {t('instruments.taskLogs.addLogger.cancel')}
        </Button>
      </div>
    </div>
  )
}

export default AddLoggerInline
